import { Controller } from "@hotwired/stimulus"
import { post, patch, destroy } from "@rails/request.js"
import Sortable from "sortablejs"
import { escapeHtml } from "../lib/html_sanitizer"
import { EMPTY_VALUE, setValueOrEmpty, valueOrEmptyHtml } from "../lib/empty_value"
import { showConfirmModal } from "../lib/custom_confirm"
import { ROW_ACTION_CLASS, ROW_ACTION_DANGER_CLASS, ROW_ACTIONS_STRIP_CLASS, fieldDeleteButtonHtml } from "../lib/row_action"
import { setStatusBadge, statusBadgeHtml } from "../lib/status_badge"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"
import { AddCustomFieldIsland } from "../lib/add_custom_field_island"

// BUTTON-9: every Save here is disabled until a field differs from what was
// loaded, and disabled again when the edit is reverted. The inline row edit
// compares its name / description / type inputs with what they held when Edit
// opened; the folder rename compares its input with the name it opened on; the
// two add forms ("dirty" on a blank create) need a name entered. Field input
// is caught by one delegated input/change listener, so the views need no
// per-input wiring. syncGateReason writes the reason tooltip and the
// "<label> — <reason>" accessible name, and puts the button's own name back
// once it is enabled.
const ROW_EDIT_FIELDS = ["nameInput", "descInput", "typeInput"]

// Below md every field also renders as a React-style mobile card (#15175,
// _field_card.html.haml) carrying the row's data-definition-id and the same
// targets. Inline edit / archive / restore / delete / Move run on whichever
// of the two the user touched (#rowFor) and are applied to both (#twins);
// add / move / drag reorder the TABLE, and #syncCards mirrors its membership
// and order into the card lists. Drag-reorder itself is desktop-only.

// Connects to data-controller="custom-fields"
//
// Manages the /settings/custom_fields screen: inline add, inline edit
// (name + description), archive/restore, delete, folder CRUD, and
// drag-and-drop reordering (both within a folder and across folders).
//
// SortableJS is initialized once on connect() against every folder <tbody>
// (group: "cf" so items can move between them) plus once on the outer
// container for folder-level reorder.
// The inline-edit type picker for a field added on this page (#16803) — the
// twin of user_settings/custom_fields/_type_input.html.haml. A new field holds
// no values yet, so its picker is never locked.
const FIELD_TYPES = ["string", "number", "money", "date", "boolean"]

function typeInputHtml(current) {
  // A Dropdown (#19714) lists its own type too, so Edit never silently picks another.
  const types = FIELD_TYPES.includes(current) ? FIELD_TYPES : [...FIELD_TYPES, current]
  const options = types.map((type) =>
    `<option value="${type}"${type === current ? " selected" : ""}>${type}</option>`).join("")
  return `<select class="gl-select-sm d-none" aria-label="Field type" data-custom-fields-target="typeInput"
    data-current-type="${escapeHtml(current)}" data-action="keydown->custom-fields#editKeydown">${options}</select>`
}

export default class extends Controller {
  static targets = [
    "tableBody", "emptyRow", "foldersContainer",
    "nameDisplay", "nameInput", "nameCell",
    "descDisplay", "descInput", "descCell",
    "editButton", "saveButton", "cancelButton",
    "archiveButton", "restoreButton", "moveSelect",
    "addFolderButton", "addFolderRow", "addFolderSubmitButton", "newFolderName",
    "folderCaret", "folderBody", "folderName", "folderCount", "folderRenameInput",
    "cardList", "cardEmpty"
  ]

  static values = {
    // JSON-encoded array of { id, name } folder records the user owns, so
    // newly-created rows can build a correct "Move to…" dropdown without a
    // round-trip back to the server.
    folderOptions: { type: String, default: "[]" }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    this.sortables = []
    this.rowBaselines = new WeakMap()
    this.folderBaselines = new WeakMap()
    this.#initSortables()
    this.#restoreCollapseState()
    this._onFieldEdit = this.#handleFieldEdit.bind(this)
    this.element.addEventListener("input", this._onFieldEdit)
    this.element.addEventListener("change", this._onFieldEdit)
    this.addField = new AddCustomFieldIsland()
    this.#syncAddFolderSave()
  }

  disconnect() {
    this.sortables.forEach(s => s.destroy())
    this.sortables = []
    this.element.removeEventListener("input", this._onFieldEdit)
    this.element.removeEventListener("change", this._onFieldEdit)
  }

  // ── Save gating (BUTTON-9) ─────────────────────────────────────────────────

  // Routes a field edit to the Save it gates. The Move select and the other
  // auto-saving controls match none of these and never touch a Save.
  #handleFieldEdit(event) {
    const el = event.target
    if (!(el instanceof Element)) return
    const target = el.getAttribute("data-custom-fields-target")
    if (ROW_EDIT_FIELDS.includes(target)) {
      const row = this.#rowFor(el)
      if (row) this.#syncRowSave(row)
    } else if (target === "folderRenameInput") {
      this.#syncFolderSave(el)
    } else if (target === "newFolderName") {
      this.#syncAddFolderSave()
    }
  }

  #rowFingerprint(row) {
    return ROW_EDIT_FIELDS
      .map(name => row.querySelector(`[data-custom-fields-target='${name}']`))
      .filter(Boolean)
      .map(fieldToken)
      .join("|")
  }

  #syncRowSave(row) {
    const saveBtn = row.querySelector("[data-custom-fields-target='saveButton']")
    if (!saveBtn) return
    const ready = this.rowBaselines.has(row) && this.#rowFingerprint(row) !== this.rowBaselines.get(row)
    syncGateReason(saveBtn, { ready, reason: SAVE_UNCHANGED_REASON })
  }

  #folderSaveButton(input) {
    const folderId = input.dataset.folderId
    return input.closest(".gl-folder-header")?.querySelector(
      `[data-action='click->custom-fields#saveFolderRename'][data-folder-id='${CSS.escape(folderId)}']`
    )
  }

  #syncFolderSave(input) {
    const saveBtn = this.#folderSaveButton(input)
    if (!saveBtn) return
    const ready = this.folderBaselines.has(input) && input.value !== this.folderBaselines.get(input)
    syncGateReason(saveBtn, { ready, reason: SAVE_UNCHANGED_REASON })
  }

  #syncAddFolderSave() {
    if (!this.hasAddFolderSubmitButtonTarget || !this.hasNewFolderNameTarget) return
    syncGateReason(this.addFolderSubmitButtonTarget, {
      ready: this.newFolderNameTarget.value.trim() !== "",
      reason: SAVE_UNCHANGED_REASON
    })
  }

  // ── Sortable init ──────────────────────────────────────────────────────────

  // One Sortable per folder <tbody> (group "cf" so cross-folder drag works) +
  // one on the outer container for folder-level reorder. The Unfiled group is
  // excluded from the outer sortable via the `draggable` selector — Unfiled
  // has no id and always renders last by convention.
  #initSortables() {
    // Field-row sortables — one per folder body (Unfiled has folder-db-id="",
    // which the server normalizes back to NULL on POST)
    this.tableBodyTargets.forEach(tbody => {
      const s = new Sortable(tbody, {
        animation: 150,
        handle: ".gl-row-drag-handle",
        group: "cf",
        draggable: "tr[data-definition-id]",
        // Within-list reorder fires onUpdate on the same list.
        // Cross-list drag (SortableJS v1.x): destination fires onAdd, source
        // fires onRemove (NOT onUpdate on source). Wire both so the source
        // tbody also gets a request that recompacts its remaining positions.
        onAdd:    (e) => this.#fieldDragLanded(e.to, e.item),
        onUpdate: (e) => this.#fieldDragLanded(e.to, e.item),
        onRemove: (e) => this.#fieldDragLanded(e.from)
      })
      this.sortables.push(s)
    })

    // Folder-level sortable — only the .gl-folder-group--sortable children are
    // draggable; the Unfiled group is just .gl-folder-group (no modifier) so
    // it sits in place as a permanent drop target.
    if (this.hasFoldersContainerTarget) {
      const s = new Sortable(this.foldersContainerTarget, {
        animation: 150,
        handle: ".gl-folder-drag-handle",
        draggable: ".gl-folder-group--sortable",
        onUpdate: this.#folderDragLanded.bind(this)
      })
      this.sortables.push(s)
    }
  }

  // POST the current ordered list of `tbody`'s definition ids to the reorder
  // endpoint along with that tbody's folder_id. Called from:
  //   - onAdd    (destination tbody, on cross-folder drag)
  //   - onUpdate (same tbody, on within-folder reorder; movedRow passed so we
  //               can also sync its "Move to…" dropdown)
  //   - onRemove (source tbody, after a cross-folder drag — recompacts positions)
  // The server transactionally updates folder_id + position for every row in
  // the payload, so an A→B move heals both A and B in two trips.
  async #fieldDragLanded(tbody, movedRow = null) {
    const folderDbId = tbody.dataset.folderDbId         // "" for Unfiled
    const ids = Array.from(tbody.querySelectorAll("tr[data-definition-id]"))
        .map(tr => tr.dataset.definitionId)

    // If the source tbody is now empty after a cross-folder drag there are no
    // positions to compact — skip the POST. (Server would reject [] anyway.)
    if (ids.length === 0) return

    // Sync the moved row's "Move to…" dropdown to the new folder so the next
    // dropdown-based move starts from the right baseline. Only set when the
    // caller passed the row (onUpdate / onAdd handlers).
    if (movedRow) {
      const select = movedRow.querySelector("[data-custom-fields-target='moveSelect']")
      if (select) select.value = folderDbId || ""
    }

    try {
      const response = await patch("/settings/custom_fields/reorder.json", {
        body: JSON.stringify({
          folder_id: folderDbId === "" ? null : Number(folderDbId),
          definition_ids: ids
        }),
        contentType: "application/json",
        responseKind: "json"
      })
      if (!response.ok) {
        const error = await response.json
        window.showToast(error.error || "Failed to reorder fields", "danger")
        location.reload()
      } else {
        this.#refreshFolderCounts()
      }
    } catch {
      window.showToast("Failed to reorder fields", "danger")
      location.reload()
    }
  }

  async #folderDragLanded() {
    const ids = Array.from(this.foldersContainerTarget.querySelectorAll(".gl-folder-group--sortable"))
        .map(el => el.dataset.id)
    try {
      const response = await patch("/settings/custom_field_folders/reorder.json", {
        body: JSON.stringify({ folder_ids: ids }),
        contentType: "application/json",
        responseKind: "json"
      })
      if (!response.ok) {
        const error = await response.json
        window.showToast(error.error || "Failed to reorder folders", "danger")
        location.reload()
      }
    } catch {
      window.showToast("Failed to reorder folders", "danger")
      location.reload()
    }
  }

  // ── Add field ──────────────────────────────────────────────────────────────

  // Add Field opens the shared Add Custom Field popup (#19714) — the same
  // one the lead panel and Import Leads use. A created field joins its
  // folder's table in place.
  openAddField() {
    this.addField.open()
  }

  // add-custom-field:ready — the island is listening.
  addFieldReady(event) {
    this.addField.ready(event.currentTarget)
  }

  // add-custom-field:created — `detail.definition` is the create's answer.
  fieldCreated(event) {
    this.#appendRow(event.detail.definition)
    this.#refreshFolderCounts()
  }

  // ── Inline edit ────────────────────────────────────────────────────────────

  startEdit(event) {
    const row = this.#rowFor(event.currentTarget)
    row.querySelector("[data-custom-fields-target='nameDisplay']")?.classList.add("d-none")
    row.querySelector("[data-custom-fields-target='nameInput']")?.classList.remove("d-none")
    row.querySelector("[data-custom-fields-target='descDisplay']")?.classList.add("d-none")
    row.querySelector("[data-custom-fields-target='descInput']")?.classList.remove("d-none")
    row.querySelector("[data-custom-fields-target='typeDisplay']")?.classList.add("d-none")
    row.querySelector("[data-custom-fields-target='typeInput']")?.classList.remove("d-none")
    this.#setEditMode(row, true)
    this.rowBaselines.set(row, this.#rowFingerprint(row))
    this.#syncRowSave(row)
    row.querySelector("[data-custom-fields-target='nameInput']")?.focus()
  }

  cancelEdit(event) {
    this.#exitEdit(this.#rowFor(event.currentTarget))
  }

  editKeydown(event) {
    const row = this.#rowFor(event.currentTarget)
    if (event.key === "Enter") {
      event.preventDefault()
      row.querySelector("[data-custom-fields-target='saveButton']")?.click()
    } else if (event.key === "Escape") {
      this.#exitEdit(row)
    }
  }

  async saveEdit(event) {
    const saveBtn = event.currentTarget
    const row     = this.#rowFor(saveBtn)
    const id      = row.dataset.definitionId
    const name    = row.querySelector("[data-custom-fields-target='nameInput']")?.value.trim()
    const desc    = row.querySelector("[data-custom-fields-target='descInput']")?.value.trim()
    // The type travels only when it changed (#16803); the server refuses it
    // for a field that holds values.
    const typeInput = row.querySelector("[data-custom-fields-target='typeInput']")
    const changedType = typeInput && !typeInput.disabled && typeInput.value !== typeInput.dataset.currentType
      ? typeInput.value
      : null

    if (!name) return

    saveBtn.disabled = true
    saveBtn.setAttribute("aria-busy", "true")

    try {
      const response = await patch(`/settings/custom_fields/${id}.json`, {
        body: JSON.stringify({
          custom_field_definition: changedType ? { name, description: desc, data_type: changedType } : { name, description: desc }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#twins(id, row).forEach(twin => {
          twin.querySelector("[data-custom-fields-target='nameDisplay']").textContent = data.name
          twin.querySelector("[data-custom-fields-target='nameInput']").value = data.name
          twin.querySelector(".gl-row-actions")?.setAttribute("aria-label", `${data.name} actions`)
          const descDisplay = twin.querySelector("[data-custom-fields-target='descDisplay']")
          setValueOrEmpty(descDisplay, data.description)
          twin.querySelector("[data-custom-fields-target='descInput']").value = data.description || ""
          this.#applyType(twin, data.data_type)
          this.#exitEdit(twin)
        })
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to update field", "danger")
      }
    } catch {
      window.showToast("Failed to update field", "danger")
    } finally {
      saveBtn.removeAttribute("aria-busy")
      this.#syncRowSave(row)
    }
  }

  // ── Archive ────────────────────────────────────────────────────────────────

  async archive(event) {
    const row  = this.#rowFor(event.currentTarget)
    const id   = row.dataset.definitionId
    const name = row.querySelector("[data-custom-fields-target='nameDisplay']")?.textContent

    const confirmed = await showConfirmModal(`Archive field "${name}"? It will no longer appear as active.`)
    if (!confirmed) return

    try {
      const response = await patch(`/settings/custom_fields/${id}/archive.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#twins(id, row).forEach(twin => this.#applyArchivedState(twin, data))
        this.#showArchivedToast(row, id, data.name)
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to archive field", "danger")
      }
    } catch {
      window.showToast("Failed to archive field", "danger")
    }
  }

  // Undo after archive (#14564, the reference reversible toast action): the
  // same authorized restore endpoint the row's Restore button calls, run once
  // through the toast's single-flight action. Archiving is durable, so there
  // is no invented expiry — the toast is held until Undo or Dismiss. The
  // server's refusals are the failure copy the user reads: "Field is not
  // archived" when the row was already restored elsewhere, the slug collision
  // when a newer field took the key; a transport failure gets the generic
  // message. The row may have left the page by the time the undo settles
  // (navigation, a re-rendered table) — the server outcome is still true and
  // still reported; only the DOM update is skipped. Names are passed raw:
  // showToast renders text through textContent, never as HTML.
  #showArchivedToast(row, id, name) {
    window.showToast(`"${name}" archived`, "success", undefined, {
      action: {
        label: "Undo",
        busyLabel: "Restoring…",
        successMessage: `"${name}" restored`,
        failureMessage: "Failed to restore field",
        run: async () => {
          const response = await patch(`/settings/custom_fields/${id}/restore.json`, {
            responseKind: "json"
          })
          const body = await response.json
          if (!response.ok) return { ok: false, message: body.error || "Failed to restore field" }
          // Both renderings of the field (table row + mobile card, #15175)
          // that are still on the page follow the restore.
          this.#twins(id, row)
            .filter(twin => twin.isConnected)
            .forEach(twin => this.#applyRestoredState(twin, body))
          return { ok: true }
        }
      }
    })
  }

  // ── Restore ────────────────────────────────────────────────────────────────

  async restore(event) {
    const row = this.#rowFor(event.currentTarget)
    const id  = row.dataset.definitionId

    try {
      const response = await patch(`/settings/custom_fields/${id}/restore.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#twins(id, row).forEach(twin => this.#applyRestoredState(twin, data))
        // Raw text: showToast renders through textContent, so escaping here
        // would show "&amp;" for a name containing "&".
        window.showToast(`"${data.name}" restored`, "success")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to restore field", "danger")
      }
    } catch {
      window.showToast("Failed to restore field", "danger")
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  async deleteDefinition(event) {
    const row  = this.#rowFor(event.currentTarget)
    const id   = row.dataset.definitionId
    const name = row.querySelector("[data-custom-fields-target='nameDisplay']")?.textContent

    const confirmed = await showConfirmModal(`Permanently delete field "${name}"? This cannot be undone.`)
    if (!confirmed) return

    try {
      const response = await destroy(`/settings/custom_fields/${id}.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        this.#twins(id, row).forEach(twin => twin.remove())
        this.#refreshFolderCounts()
        window.showToast(`"${name}" deleted`, "success")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to delete field", "danger")
      }
    } catch {
      window.showToast("Failed to delete field", "danger")
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  // The field's desktop row or its mobile card — whichever holds `element`.
  #rowFor(element) {
    return element.closest("tr, .gl-mobile-card")
  }

  // Every rendering of one field: its row and its card. `fallback` keeps a
  // lone element working when its twin is missing (a hand-built fixture).
  #twins(id, fallback) {
    const selector = `tr[data-definition-id='${CSS.escape(String(id))}'], ` +
      `.gl-mobile-card[data-definition-id='${CSS.escape(String(id))}']`
    const found = Array.from(this.element.querySelectorAll(selector))
    return found.length > 0 ? found : [fallback]
  }

  #tableRowFor(id) {
    return this.element.querySelector(`tr[data-definition-id='${CSS.escape(String(id))}']`)
  }

  // Mirror each folder table's membership and order into its card list and
  // sync every card's Move select to the folder it now sits in, then show the
  // card list's empty state exactly when the folder has no fields.
  #syncCards() {
    if (!this.hasCardListTarget) return
    this.tableBodyTargets.forEach(tbody => {
      const key  = tbody.dataset.folderId
      const list = this.cardListTargets.find(l => l.dataset.folderId === key)
      if (!list) return
      const ids = Array.from(tbody.querySelectorAll("tr[data-definition-id]")).map(tr => tr.dataset.definitionId)
      ids.forEach(id => {
        const card = this.element.querySelector(`.gl-mobile-card[data-definition-id='${CSS.escape(id)}']`)
        if (card) list.appendChild(card)
        // Both twins' Move selects name the folder the row now sits in — a
        // move made on the card leaves the row's select behind otherwise.
        this.#twins(id, card).forEach(twin => {
          const select = twin?.querySelector("[data-custom-fields-target='moveSelect']")
          if (select) select.value = tbody.dataset.folderDbId || ""
        })
      })
      const empty = this.cardEmptyTargets.find(e => e.dataset.folderId === key)
      if (empty) empty.hidden = ids.length > 0
    })
  }

  // The card's one pill (the React MobileCard badge slot) follows the row's
  // status pill on archive / restore.
  #setCardBadge(card, label, variant) {
    const badge = card.querySelector(".gl-mc-badge")
    if (!badge) return
    badge.className = `gl-mc-badge gl-mc-badge--${variant}`
    badge.textContent = label
  }

  #setEditMode(row, editing) {
    row.querySelector("[data-custom-fields-target='editButton']")?.classList.toggle("d-none", editing)
    row.querySelector("[data-custom-fields-target='saveButton']")?.classList.toggle("d-none", !editing)
    row.querySelector("[data-custom-fields-target='cancelButton']")?.classList.toggle("d-none", !editing)
    row.querySelector("[data-custom-fields-target='archiveButton']")?.classList.toggle("d-none", editing)
    row.querySelector("[data-custom-fields-target='restoreButton']")?.classList.toggle("d-none", editing)
  }

  // A saved type (#16803): the badge text and colour, and the picker's
  // baseline for the next edit.
  #applyType(row, dataType) {
    const badge = row.querySelector("[data-custom-fields-target='typeDisplay']")
    if (badge) {
      badge.textContent = dataType
      badge.className = `gl-field-type-badge gl-field-type-badge--${dataType}`
    }
    const typeInput = row.querySelector("[data-custom-fields-target='typeInput']")
    if (typeInput) typeInput.dataset.currentType = dataType
  }

  #exitEdit(row) {
    // Reset each input back to its paired display value before hiding it.
    // Without this, an unsaved typed value persists in the hidden input;
    // reopening edit would show the stale value and Enter/Save would
    // silently submit it.
    const nameDisplay = row.querySelector("[data-custom-fields-target='nameDisplay']")
    const nameInput   = row.querySelector("[data-custom-fields-target='nameInput']")
    const descDisplay = row.querySelector("[data-custom-fields-target='descDisplay']")
    const descInput   = row.querySelector("[data-custom-fields-target='descInput']")

    if (nameInput && nameDisplay) nameInput.value = nameDisplay.textContent
    if (descInput && descDisplay) {
      // descDisplay shows the empty-value marker when empty; mirror that back
      // to the input as "" (server-rendered rows carry HAML whitespace)
      const text = descDisplay.textContent.trim()
      descInput.value = text === EMPTY_VALUE ? "" : text
    }

    const typeInput = row.querySelector("[data-custom-fields-target='typeInput']")
    if (typeInput) typeInput.value = typeInput.dataset.currentType

    nameDisplay?.classList.remove("d-none")
    nameInput?.classList.add("d-none")
    descDisplay?.classList.remove("d-none")
    descInput?.classList.add("d-none")
    row.querySelector("[data-custom-fields-target='typeDisplay']")?.classList.remove("d-none")
    typeInput?.classList.add("d-none")
    this.#setEditMode(row, false)
  }

  #applyArchivedState(row, data) {
    row.dataset.archived = "true"
    row.classList.add("gl-row-archived")
    const statusCell = row.querySelector(".gl-status-badge")
    if (statusCell) setStatusBadge(statusCell, "Archived", "secondary")
    this.#setCardBadge(row, "Archived", "inactive")
    const actionsDiv = this.#rowActionsStrip(row)
    if (!actionsDiv) return
    row.querySelectorAll("[data-action='click->custom-fields#deleteDefinition']").forEach(b => b.remove())
    row.querySelector("[data-custom-fields-target='archiveButton']")?.remove()
    const html = `
      <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Restore" aria-label="Restore"
              data-action="click->custom-fields#restore"
              data-custom-fields-target="restoreButton">
        <i class="ph ph-arrow-counter-clockwise"></i>
      </button>
      ${fieldDeleteButtonHtml("click->custom-fields#deleteDefinition", data.has_data)}
    `
    actionsDiv.insertAdjacentHTML("beforeend", html)
  }

  #applyRestoredState(row, data) {
    row.dataset.archived = "false"
    row.classList.remove("gl-row-archived")
    const statusCell = row.querySelector(".gl-status-badge")
    if (statusCell) setStatusBadge(statusCell, "Active", "success")
    this.#setCardBadge(row, "Active", "success")
    const actionsDiv = this.#rowActionsStrip(row)
    if (!actionsDiv) return
    row.querySelectorAll("[data-action='click->custom-fields#restore']").forEach(b => b.remove())
    row.querySelectorAll("[data-action='click->custom-fields#deleteDefinition']").forEach(b => b.remove())
    const html = `
      <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Archive" aria-label="Archive"
              data-action="click->custom-fields#archive"
              data-custom-fields-target="archiveButton">
        <i class="ph ph-archive"></i>
      </button>
      ${fieldDeleteButtonHtml("click->custom-fields#deleteDefinition", data.has_data)}
    `
    actionsDiv.insertAdjacentHTML("beforeend", html)
  }

  // Find the <tbody> for a given folder_id (null/undefined => Unfiled).
  // Returns null if no matching body exists (which shouldn't happen since
  // Unfiled always renders).
  #tbodyFor(folderId) {
    const targetKey = (folderId === null || folderId === undefined) ? "unfiled" : String(folderId)
    return this.tableBodyTargets.find(tb => tb.dataset.folderId === targetKey) || null
  }

  // Folder-row count chip is rendered server-side from the initial row count.
  // Recompute and update each chip after any drag / add / delete so the
  // numbers stay honest. Also hides/shows the empty-row placeholder.
  #refreshFolderCounts() {
    this.#syncCards()
    this.tableBodyTargets.forEach(tbody => {
      const key   = tbody.dataset.folderId
      const count = tbody.querySelectorAll("tr[data-definition-id]").length
      const chip  = this.folderCountTargets.find(c => c.dataset.folderId === key)
      if (chip) chip.textContent = `(${count})`

      // Empty-row placeholder management. We can't use the emptyRow target
      // here because it's per-tbody, not unique — so we look up by dataset.
      const placeholder = tbody.querySelector("tr[data-custom-fields-target='emptyRow']")
      if (count > 0 && placeholder) placeholder.remove()
      if (count === 0 && !placeholder) {
        const folderId = tbody.dataset.folderId
        // Mirrors _folder_group.html.haml: with no folders the Unfiled group is
        // simply "Fields", so its empty copy names Add Field, not drag/Move.
        // Same markup as shared/_empty_state (icon + title + hint, #15172).
        const dragHint = "Drag a field here, or use the Move action."
        const [icon, title, hint] = folderId === "unfiled"
            ? (this.#folderOptions().length === 0
                ? ["ph-textbox", "No fields yet", "Use Add Field to create one."]
                : ["ph-tray", "No unfiled fields", dragHint])
            : ["ph-folder-open", "Empty folder", dragHint]
        tbody.insertAdjacentHTML("beforeend", `
          <tr data-custom-fields-target="emptyRow" data-folder-id="${escapeHtml(folderId)}">
            <td colspan="7">
              <div class="gl-empty-state">
                <i class="ph ${icon}" aria-hidden="true"></i>
                <p>${title}</p>
                <p class="gl-empty-state__hint">${hint}</p>
              </div>
            </td>
          </tr>
        `)
      }
    })
  }

  #folderOptions() {
    try {
      return JSON.parse(this.folderOptionsValue || "[]")
    } catch {
      return []
    }
  }

  #moveSelectOptionsHtml(currentFolderId) {
    const folders  = this.#folderOptions()
    const unfiled  = `<option value="" ${currentFolderId == null ? "selected" : ""}>Unfiled</option>`
    const items    = folders.map(f =>
      `<option value="${f.id}" ${String(currentFolderId) === String(f.id) ? "selected" : ""}>${escapeHtml(f.name)}</option>`
    ).join("")
    return unfiled + items
  }

  // The row's action run (gl-row-actions). When it is missing, the caller leaves the
  // row's existing buttons in place (never strips them with nothing to put
  // back) and the user is told to reload.
  #rowActionsStrip(row) {
    const strip = row.querySelector(".gl-row-actions")
    if (!strip) window.showToast("Saved. Reload the page to see this field's updated actions.", "warning")
    return strip
  }

  #appendRow(data) {
    const tbody = this.#tbodyFor(data.folder_id)
    // Edge case: folder was deleted in another tab so the destination tbody
    // no longer exists in our DOM. Reload so the server-rendered group set
    // matches reality (and the new field shows up in the right place).
    if (!tbody) {
      window.showToast("Refreshing folder list…", "info")
      location.reload()
      return
    }

    // Remove the empty-row placeholder for this tbody, if present.
    tbody.querySelector("tr[data-custom-fields-target='emptyRow']")?.remove()

    const typeClass = `gl-field-type-badge--${escapeHtml(data.data_type)}`
    const showMove  = this.#folderOptions().length > 0 || data.folder_id != null
    const moveHtml  = showMove
        ? `<select class="gl-select-sm gl-move-select"
                   data-action="change->custom-fields#moveToFolder"
                   data-custom-fields-target="moveSelect"
                   data-definition-id="${data.id}"
                   aria-label="Move to folder">
             ${this.#moveSelectOptionsHtml(data.folder_id)}
           </select>`
        : ""
    const html = `
      <tr id="custom-field-${data.id}"
          data-id="${data.id}"
          data-definition-id="${data.id}"
          data-has-data="${data.has_data}"
          data-archived="false">
        <td class="ps-3 gl-row-drag-handle" data-gl-tooltip="Drag to reorder" aria-label="Drag to reorder">
          <i class="ph ph-dots-six-vertical"></i>
        </td>
        <td>
          <strong data-custom-fields-target="nameDisplay">${escapeHtml(data.name)}</strong>
          <input type="text" class="gl-form-control d-none" value="${escapeHtml(data.name)}"
                 maxlength="255"
                 data-custom-fields-target="nameInput"
                 data-action="keydown->custom-fields#editKeydown">
        </td>
        <td><span class="gl-field-key">${escapeHtml(data.slug)}</span></td>
        <td>
          <span class="gl-field-type-badge ${typeClass}" data-custom-fields-target="typeDisplay">${escapeHtml(data.data_type)}</span>
          ${typeInputHtml(data.data_type)}
        </td>
        <td>
          <span class="text-body-secondary" data-custom-fields-target="descDisplay">${valueOrEmptyHtml(data.description)}</span>
          <input type="text" class="gl-form-control d-none" value="${escapeHtml(data.description || "")}"
                 maxlength="500"
                 data-custom-fields-target="descInput"
                 data-action="keydown->custom-fields#editKeydown">
        </td>
        <td>${statusBadgeHtml("Active", "success")}</td>
        <td class="pe-3">
          <div class="gl-table-actions">
            ${this.#actionsHtml(data, moveHtml)}
          </div>
        </td>
      </tr>
    `
    tbody.insertAdjacentHTML("beforeend", html)
    this.#appendCard(data, moveHtml)
  }

  // A client-added field's mobile card — the anatomy of _field_card.html.haml
  // (MobileCardHelper#gl_mobile_card). #syncCards places it in its folder.
  #appendCard(data, moveHtml) {
    const list = this.cardListTargets.find(l => l.dataset.folderId === (data.folder_id == null ? "unfiled" : String(data.folder_id)))
    if (!list) return
    list.insertAdjacentHTML("beforeend", `
      <div class="gl-mobile-card gl-mobile-card--static" role="listitem" id="custom-field-card-${data.id}"
           data-definition-id="${data.id}" data-has-data="${data.has_data}" data-archived="false">
        <div class="gl-mc-primary">
          <strong data-custom-fields-target="nameDisplay">${escapeHtml(data.name)}</strong>
          <input type="text" class="gl-form-control d-none" value="${escapeHtml(data.name)}" maxlength="255"
                 aria-label="Field name" data-custom-fields-target="nameInput"
                 data-action="keydown->custom-fields#editKeydown">
        </div>
        <span class="gl-mc-badge gl-mc-badge--success">Active</span>
        <div class="gl-mc-secondary"><span class="gl-mc-seg gl-mc-seg--text"><span>${escapeHtml(data.slug)}</span></span> <span class="gl-mc-seg gl-mc-seg--text"><span class="gl-mc-sep" aria-hidden="true">·</span><span>${escapeHtml(data.data_type)}</span></span></div>
        <dl class="gl-mc-details">
          <div class="gl-mc-detail">
            <dt class="gl-mc-detail-label">Type</dt>
            <dd class="gl-mc-detail-value">
              <span class="gl-field-type-badge gl-field-type-badge--${escapeHtml(data.data_type)}" data-custom-fields-target="typeDisplay">${escapeHtml(data.data_type)}</span>
              ${typeInputHtml(data.data_type)}
            </dd>
          </div>
          <div class="gl-mc-detail">
            <dt class="gl-mc-detail-label">Description</dt>
            <dd class="gl-mc-detail-value">
              <span class="text-body-secondary" data-custom-fields-target="descDisplay">${valueOrEmptyHtml(data.description)}</span>
              <input type="text" class="gl-form-control d-none" value="${escapeHtml(data.description || "")}" maxlength="500"
                     aria-label="Field description" data-custom-fields-target="descInput"
                     data-action="keydown->custom-fields#editKeydown">
            </dd>
          </div>
        </dl>
        <div class="gl-mc-actions">${this.#actionsHtml(data, moveHtml)}</div>
      </div>
    `)
  }

  // The Move select + row-action run of _field_actions.html.haml, for a row or
  // card the controller builds.
  #actionsHtml(data, moveHtml) {
    return `
            ${moveHtml}
            <div class="${ROW_ACTIONS_STRIP_CLASS}" role="group" aria-label="${escapeHtml(data.name)} actions">
            <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Edit" aria-label="Edit"
                    data-action="click->custom-fields#startEdit"
                    data-custom-fields-target="editButton">
              <i class="ph ph-pencil-simple"></i>
            </button>
            <button class="${ROW_ACTION_CLASS} d-none" type="button" data-gl-tooltip="Save" aria-label="Save"
                    data-action="click->custom-fields#saveEdit"
                    data-custom-fields-target="saveButton">
              <i class="ph ph-check"></i>
            </button>
            <button class="${ROW_ACTION_CLASS} d-none" type="button" data-gl-tooltip="Cancel" aria-label="Cancel"
                    data-action="click->custom-fields#cancelEdit"
                    data-custom-fields-target="cancelButton">
              <i class="ph ph-x"></i>
            </button>
            <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Archive" aria-label="Archive"
                    data-action="click->custom-fields#archive"
                    data-custom-fields-target="archiveButton">
              <i class="ph ph-archive"></i>
            </button>
            <button class="${ROW_ACTION_DANGER_CLASS}" type="button" data-gl-tooltip="Delete" aria-label="Delete"
                    data-action="click->custom-fields#deleteDefinition">
              <i class="ph ph-trash"></i>
            </button>
            </div>`
  }

  // ── Move field to a different folder (dropdown path) ───────────────────────

  async moveToFolder(event) {
    const select   = event.currentTarget
    const id       = this.#rowFor(select).dataset.definitionId
    // The TABLE row is what moves (a card's twin included): #refreshFolderCounts
    // then mirrors the move into the card lists.
    const row      = this.#tableRowFor(id) || this.#rowFor(select)
    const folderId = select.value === "" ? null : Number(select.value)

    try {
      const response = await patch(`/settings/custom_fields/${id}.json`, {
        body: JSON.stringify({
          custom_field_definition: { folder_id: folderId }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        // Physically move the row into the target tbody so visual state
        // matches the server, then recompute counts/empty-row placeholders.
        const target = this.#tbodyFor(folderId)
        if (target && row.matches("tr")) {
          target.querySelector("tr[data-custom-fields-target='emptyRow']")?.remove()
          target.appendChild(row)
        }
        this.#refreshFolderCounts()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to move field", "danger")
        select.value = row.closest("tbody")?.dataset.folderDbId || ""
      }
    } catch {
      window.showToast("Failed to move field", "danger")
    }
  }

  // ── Add folder ─────────────────────────────────────────────────────────────

  showAddFolderForm() {
    this.addFolderRowTarget.classList.remove("d-none")
    this.addFolderButtonTarget.classList.add("d-none")
    this.newFolderNameTarget.value = ""
    this.#syncAddFolderSave()
    this.newFolderNameTarget.focus()
  }

  cancelAddFolder() {
    this.addFolderRowTarget.classList.add("d-none")
    this.addFolderButtonTarget.classList.remove("d-none")
    this.newFolderNameTarget.value = ""
    this.#syncAddFolderSave()
  }

  folderFormKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitAddFolder()
    } else if (event.key === "Escape") {
      this.cancelAddFolder()
    }
  }

  async submitAddFolder() {
    const name = this.newFolderNameTarget.value.trim()
    if (!name) return

    this.addFolderSubmitButtonTarget.disabled = true
    try {
      const response = await post("/settings/custom_field_folders.json", {
        body: JSON.stringify({ folder: { name } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        // Server confirmed; reload so the new group renders in the right
        // position. (Building the full group DOM client-side here would
        // duplicate a meaningful chunk of folder_group.html.haml — not
        // worth the maintenance cost for an admin-frequency action.)
        location.reload()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to add folder", "danger")
      }
    } catch {
      window.showToast("Failed to add folder", "danger")
    } finally {
      this.#syncAddFolderSave()
    }
  }

  // ── Rename folder (inline) ─────────────────────────────────────────────────

  startFolderRename(event) {
    const folderId = event.currentTarget.dataset.folderId
    const header   = event.currentTarget.closest(".gl-folder-header")
    if (!header) return
    header.querySelector(`.gl-folder-name[data-folder-id='${CSS.escape(folderId)}']`)?.classList.add("d-none")
    header.querySelector(`.gl-form-control[data-folder-id='${CSS.escape(folderId)}']`)?.classList.remove("d-none")
    const input = header.querySelector(`input[data-folder-id='${CSS.escape(folderId)}']`)
    input?.focus()
    input?.select()
    this.#setFolderEditMode(header, true)
    if (input) {
      this.folderBaselines.set(input, input.value)
      this.#syncFolderSave(input)
    }
  }

  cancelFolderRename(event) {
    const folderId = event.currentTarget.dataset.folderId
    const header   = event.currentTarget.closest(".gl-folder-header")
    if (!header) return
    const nameEl  = header.querySelector(`.gl-folder-name[data-folder-id='${CSS.escape(folderId)}']`)
    const input   = header.querySelector(`input[data-folder-id='${CSS.escape(folderId)}']`)
    if (input && nameEl) input.value = nameEl.textContent.trim()
    input?.classList.add("d-none")
    nameEl?.classList.remove("d-none")
    this.#setFolderEditMode(header, false)
  }

  folderRenameKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const folderId = event.currentTarget.dataset.folderId
      const header   = event.currentTarget.closest(".gl-folder-header")
      header?.querySelector(
        `[data-action='click->custom-fields#saveFolderRename'][data-folder-id='${CSS.escape(folderId)}']`
      )?.click()
    } else if (event.key === "Escape") {
      this.cancelFolderRename(event)
    }
  }

  async saveFolderRename(event) {
    // Capture currentTarget synchronously — it's nulled out by the DOM after
    // the handler returns, so we can't rely on it after the first `await`.
    const saveButton = event.currentTarget
    const folderId   = saveButton.dataset.folderId
    const header     = saveButton.closest(".gl-folder-header")
    const input      = header?.querySelector(`input[data-folder-id='${CSS.escape(folderId)}']`)
    const name       = input?.value.trim()
    if (!name) return

    saveButton.disabled = true
    try {
      const response = await patch(`/settings/custom_field_folders/${folderId}.json`, {
        body: JSON.stringify({ folder: { name } }),
        contentType: "application/json",
        responseKind: "json"
      })
      if (response.ok) {
        const data   = await response.json
        const nameEl = header.querySelector(`.gl-folder-name[data-folder-id='${CSS.escape(folderId)}']`)
        if (nameEl) nameEl.textContent = data.name
        // Keep the "Move to…" dropdowns and the new-field folder picker in
        // sync with the renamed folder.
        this.#updateFolderOptionsCache(data)
        this.#refreshAllMoveSelects()
        this.#exitFolderRename(header)
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to rename folder", "danger")
      }
    } catch {
      window.showToast("Failed to rename folder", "danger")
    } finally {
      if (input) this.#syncFolderSave(input)
    }
  }

  // Internal version of cancelFolderRename that doesn't require an event —
  // called from the async saveFolderRename success path where currentTarget
  // is no longer reliable.
  #exitFolderRename(header) {
    if (!header) return
    header.querySelectorAll(".gl-form-control").forEach(el => el.classList.add("d-none"))
    header.querySelectorAll(".gl-folder-name").forEach(el => el.classList.remove("d-none"))
    this.#setFolderEditMode(header, false)
  }

  // ── Delete folder ──────────────────────────────────────────────────────────

  async deleteFolder(event) {
    const folderId = event.currentTarget.dataset.folderId
    const header   = event.currentTarget.closest(".gl-folder-header")
    const name     = header?.querySelector(`.gl-folder-name[data-folder-id='${CSS.escape(folderId)}']`)?.textContent.trim()
    const tbody    = this.tableBodyTargets.find(t => t.dataset.folderId === folderId)
    const count    = tbody?.querySelectorAll("tr[data-definition-id]").length || 0
    const message  = count > 0
        ? `Delete folder "${name}"? Its ${count} field${count === 1 ? "" : "s"} will move to Unfiled.`
        : `Delete folder "${name}"?`

    const confirmed = await showConfirmModal(message)
    if (!confirmed) return

    try {
      const response = await destroy(`/settings/custom_field_folders/${folderId}.json`, {
        responseKind: "json"
      })
      if (response.ok) {
        // Server unfiled the fields server-side. Reload so the DOM lines up.
        location.reload()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to delete folder", "danger")
      }
    } catch {
      window.showToast("Failed to delete folder", "danger")
    }
  }

  // ── Collapse/expand folder body ────────────────────────────────────────────

  toggleFolder(event) {
    const folderId = event.currentTarget.dataset.folderId
    const body  = this.folderBodyTargets.find(b => b.dataset.folderId === folderId)
    const caret = this.folderCaretTargets.find(c => c.dataset.folderId === folderId)
    if (!body) return

    const willCollapse = !body.classList.contains("d-none")
    body.classList.toggle("d-none", willCollapse)
    caret?.classList.toggle("ph-caret-right", willCollapse)
    caret?.classList.toggle("ph-caret-down", !willCollapse)

    this.#persistCollapseState(folderId, willCollapse)
  }

  // ── Private helpers (folders) ──────────────────────────────────────────────

  #setFolderEditMode(header, editing) {
    header.querySelectorAll("[data-action='click->custom-fields#startFolderRename']")
        .forEach(b => b.classList.toggle("d-none", editing))
    header.querySelectorAll("[data-action='click->custom-fields#deleteFolder']")
        .forEach(b => b.classList.toggle("d-none", editing))
    header.querySelectorAll("[data-action='click->custom-fields#saveFolderRename']")
        .forEach(b => b.classList.toggle("d-none", !editing))
    header.querySelectorAll("[data-action='click->custom-fields#cancelFolderRename']")
        .forEach(b => b.classList.toggle("d-none", !editing))
  }

  #updateFolderOptionsCache(folder) {
    const options = this.#folderOptions()
    const existing = options.find(o => String(o.id) === String(folder.id))
    if (existing) {
      existing.name = folder.name
    } else {
      options.push({ id: folder.id, name: folder.name })
    }
    this.folderOptionsValue = JSON.stringify(options)
  }

  // Rebuild every "Move to…" select's <option> list so renamed folders show
  // their new name. Preserves the currently-selected folder per row by saving
  // and restoring `select.value` around the innerHTML rewrite — we don't need
  // the rendered `selected` attribute to be correct since `value = currentValue`
  // overrides it immediately. Pass null so the helper doesn't render any
  // misleading `selected` flag in the new option markup.
  #refreshAllMoveSelects() {
    this.moveSelectTargets.forEach(select => {
      const currentValue = select.value
      select.innerHTML = this.#moveSelectOptionsHtml(null)
      select.value = currentValue
    })
  }

  #persistCollapseState(folderId, collapsed) {
    try {
      const key   = "cf:collapsed-folders"
      const state = JSON.parse(localStorage.getItem(key) || "{}")
      if (collapsed) state[folderId] = true; else delete state[folderId]
      localStorage.setItem(key, JSON.stringify(state))
    } catch { /* localStorage unavailable — ignore */ }
  }

  #restoreCollapseState() {
    try {
      const state = JSON.parse(localStorage.getItem("cf:collapsed-folders") || "{}")
      Object.keys(state).forEach(folderId => {
        const body  = this.folderBodyTargets.find(b => b.dataset.folderId === folderId)
        const caret = this.folderCaretTargets.find(c => c.dataset.folderId === folderId)
        if (body) body.classList.add("d-none")
        caret?.classList.add("ph-caret-right")
        caret?.classList.remove("ph-caret-down")
      })
    } catch { /* ignore */ }
  }
}
